import { ArrowLeft } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Show } from "@/components/atoms/show";
import FileUpload from "@/components/molecules/file-upload";
import { CollectionFormScreenProps } from "../../types";
import { getCollectionFormStyles } from "../../utils/extract-review-form-styles";
import { TestimonialFormTypes } from "../../utils/schema";
import { StarRating } from "@/components/atoms/rating";
import { useToast } from "@/hooks/use-toast";

export const TextResponseScreen = ({
  data,
  onChangeStep,
}: CollectionFormScreenProps) => {
  const form = useFormContext<TestimonialFormTypes>();
  const styles = getCollectionFormStyles(data);
  const { toast } = useToast();

  const onNextStep = () => {
    if (!form.watch("rating")) {
      toast({ description: "Please rate us before proceeding" });
      console.log("Please rate us before proceeding");
      return;
    }

    if (!form.watch("content")) {
      toast({ description: "Please share your experience before proceeding" });
      return;
    }

    onChangeStep("customerForm");
    form.setValue("type", "text");
    form.setValue("source", "text");
  };

  return (
    <Card
      className={cn(styles.cardPadding, styles.cardSpacing, "testify-max-w-xl")}
      style={{
        backgroundColor: styles["secondaryColor"],
        color: styles["secondaryTextColor"],
      }}
    >
      {/* Header */}
      <div className="testify-space-y-4">
        <div className="testify-flex testify-items-center testify-gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChangeStep("welcome")}
          >
            <ArrowLeft className="testify-h-5 testify-w-5" />
          </Button>
          <CardTitle className="testify-text-xl sm:testify-text-2xl md:testify-text-3xl">
            {data.responsePage.title}
          </CardTitle>
        </div>

        <CardDescription
          className="testify-text-base sm:testify-text-lg md:testify-text-xl testify-leading-relaxed"
          dangerouslySetInnerHTML={{ __html: data.responsePage.prompt }}
        />
      </div>

      <div className="testify-space-y-6">
        {/* Rating Section */}
        <Show when={data.responsePage.shouldCollectStars}>
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem className="testify-space-y-2">
                <FormLabel className="testify-text-base sm:testify-text-lg">
                  {data.labels.rating}
                </FormLabel>
                <FormControl>
                  <div className="testify-flex testify-items-center testify-gap-2">
                    <StarRating
                      size="lg"
                      value={Number(field.value) || 0}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span className="testify-text-sm testify-text-muted-foreground">
                      {Number(field.value) || 0}/5
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>

        {/* Testimonial Text */}
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="testify-space-y-2">
              <FormLabel className="testify-text-base sm:testify-text-lg">
                {data.labels.question}
              </FormLabel>
              <FormControl>
                <Textarea
                  className="!testify-min-h-[150px] testify-resize-none"
                  placeholder="Share your experience..."
                  maxLength={data.settings.textCharacterLimit}
                  {...field}
                />
              </FormControl>
              <div className="testify-flex testify-justify-end">
                <span className="testify-text-sm testify-text-muted-foreground">
                  {field.value?.length || 0}/{data.settings.textCharacterLimit}
                </span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Attachments Section */}
        <Show when={data.responsePage.allowAttachments}>
          <FormField
            control={form.control}
            name="media"
            render={({ field }) => (
              <FormItem className="testify-space-y-4">
                <div className="testify-flex testify-items-center testify-justify-between">
                  <FormLabel className="testify-text-base sm:testify-text-lg">
                    {data.labels.attachments || "Add Attachments"}
                  </FormLabel>
                  <FormDescription>
                    Max 3 files (Images or PDFs)
                  </FormDescription>
                </div>

                <div className="testify-space-y-4">
                  {/* File Upload Zone */}
                  <FileUpload
                    name={field.name}
                    maxFiles={3}
                    maxSize={5 * 1024 * 1024} // 5MB
                    accept={{
                      "image/*": [".png", ".jpg", ".jpeg"],
                      "application/pdf": [".pdf"],
                    }}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </Show>

        {/* Submit Button */}
        <Button
          className="testify-w-full testify-cursor-pointer"
          size="lg"
          onClick={onNextStep}
          // disabled={!form.watch("content")}
          style={{
            background: styles["primaryColor"],
            color: styles["primaryTextColor"],
          }}
        >
          {data.labels.submitButton}
        </Button>
      </div>
    </Card>
  );
};
